/**
 * ConfirmCriticalAction — S3.10A.
 *
 * Confirmação forte para AÇÕES CRÍTICAS de dispositivo (relé OFF, reiniciar
 * relé, reiniciar ESP32) — individual e em massa. O backend exige a credencial
 * no request; aqui coletamos a confirmação de forma reutilizável:
 *
 *  - Usuário logado (Bearer): re-digita a PRÓPRIA senha.
 *  - Bancada (X-Admin-Token, sem sessão): re-digita o ADMIN_TOKEN.
 *
 * A credencial NUNCA é salva — vai só no callback `onConfirm(secret)` e o campo
 * é limpo logo após. Uso via hook:
 *
 *    const confirmCritical = useCriticalConfirm();
 *    const secret = await confirmCritical({ actionLabel, target, impact });
 *    if (secret == null) return;            // cancelado
 *    await api(..., { confirm: secret });   // backend valida e bloqueia se errado
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { isRecoveryActive, loadSession } from "../lib/auth.js";
import { loadConfig } from "../lib/config.js";
import BulkProtectionBreakdown from "./BulkProtectionBreakdown.jsx";

const Ctx = createContext(() => Promise.resolve(null));

/** Hook: retorna requestConfirm(descriptor) => Promise<secret|null>. */
export const useCriticalConfirm = () => useContext(Ctx);

/** Modo de confirmação atual: recovery (ADMIN_TOKEN) vs sessão (senha). */
export function confirmMode() {
  // SAAS-AUTH-BEARER-ENFORCEMENT — bancada/recovery só conta com recovery ativo;
  // ADMIN_TOKEN no localStorage sem recovery NÃO muda o modo (fica em senha).
  const bench = !loadSession()?.token && isRecoveryActive() && !!loadConfig().adminToken;
  return {
    bench,
    label: bench ? "ADMIN_TOKEN" : "Senha",
    placeholder: bench ? "Re-digite o ADMIN_TOKEN" : "Sua senha de acesso",
  };
}

export function CriticalConfirmProvider({ children }) {
  const [req, setReq] = useState(null); // { d, resolve }
  const requestConfirm = useCallback(
    (d) => new Promise((resolve) => setReq({ d, resolve })),
    [],
  );
  const settle = useCallback((secret, reason) => {
    setReq((r) => {
      // S3.11B — quando o descriptor pede motivo (collectReason), resolve um
      // objeto { secret, reason }; senão mantém o contrato antigo (string
      // secret) para não quebrar os demais call sites (relé/reboot/agenda).
      if (r?.d?.collectReason) {
        r?.resolve(secret == null ? null : { secret, reason: reason ?? "" });
      } else {
        r?.resolve(secret);
      }
      return null;
    });
  }, []);
  return (
    <Ctx.Provider value={requestConfirm}>
      {children}
      {req && (
        <ConfirmCriticalAction
          descriptor={req.d}
          onConfirm={(s, reason) => settle(s, reason)}
          onCancel={() => settle(null)}
        />
      )}
    </Ctx.Provider>
  );
}

function ConfirmCriticalAction({ descriptor, onConfirm, onCancel }) {
  const {
    title = "Confirmar ação crítica",
    actionLabel,
    target = {},
    impact,
    tenant,
    requirePassword = true,
    collectReason = false,
    reasonLabel = "Motivo (opcional)",
    reasonPlaceholder = "",
    reasonRequired = false,
    reasonDefault = "",
  } = descriptor || {};
  const mode = confirmMode();
  const [secret, setSecret] = useState("");
  const [reason, setReason] = useState(reasonDefault);
  const inputRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(id);
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const isBulk = target.kind === "bulk";
  const count = target.count || 0;
  // MAP-UX-1E — quando o call site fornece o recorte de proteção, exibe o
  // breakdown (total/elegíveis/protegidos/offline) DENTRO do modal, acima do
  // campo de senha. O modal cobre a BulkActionBar (overlay z-4000), então o
  // chip de protegidos da barra fica escondido na hora de confirmar.
  const hasBreakdown = isBulk && typeof target.eligible === "number";
  const protectedCount = target.protectedCount || 0;
  const canConfirm =
    (!requirePassword || secret.length > 0) &&
    (!collectReason || !reasonRequired || reason.trim().length > 0);

  function submit(e) {
    e?.preventDefault();
    if (!canConfirm) return;
    const s = secret;
    const r = reason.trim();
    setSecret(""); // limpa imediatamente — nunca persiste
    onConfirm(requirePassword ? s : "", r);
  }

  // S3.10B fix — portaliza para document.body com z-index topo: rendido inline
  // no root, o modal ficava preso atrás do DeviceConfigModal (z-2000) quando
  // disparado de dentro do stacking context do mapa Leaflet. No body + z-[4000]
  // ele fica acima de QUALQUER modal (config z-2000, bulk-reboot z-3000).
  return createPortal(
    <div
      className="fixed inset-0 z-[4000] bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-xl border border-noc-err/40
                   bg-noc-panel p-5 space-y-3 shadow-2xl"
      >
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 font-mono text-[10px]
                          uppercase tracking-[0.2em] text-noc-err">
            <span className="led-err animate-wd-blink" />
            Ação crítica
          </div>
          <h2 className="text-base font-bold text-noc-text">{title}</h2>
        </div>

        {actionLabel && (
          <div className="text-sm text-noc-text">
            Ação: <span className="font-semibold">{actionLabel}</span>
          </div>
        )}

        {/* Alvo: device único ou em massa */}
        {isBulk ? (
          <div className="rounded-lg border border-noc-err/40 bg-noc-err/10 px-3 py-2 space-y-2">
            <div className="text-sm text-noc-err font-medium">
              {hasBreakdown && protectedCount > 0 ? (
                <>
                  Você está prestes a aplicar esta ação em{" "}
                  <span className="font-bold">{target.eligible}</span> de{" "}
                  <span className="font-bold">{count}</span> selecionado
                  {count !== 1 ? "s" : ""}.
                </>
              ) : (
                <>
                  Você está prestes a aplicar esta ação em{" "}
                  <span className="font-bold">{count}</span> dispositivo
                  {count !== 1 ? "s" : ""}.
                </>
              )}
            </div>
            <div className="text-xs text-noc-err/90 font-semibold">
              Esta ação pode derrubar vários clientes/dispositivos.
            </div>
            {hasBreakdown && (
              <BulkProtectionBreakdown
                total={count}
                eligible={target.eligible}
                protectedCount={protectedCount}
                protectedIds={target.protectedIds || []}
                offlineCount={target.offlineCount || 0}
                actionLabel="este comando"
              />
            )}
            {Array.isArray(target.names) && target.names.length > 0 && (
              <div className="text-[10px] text-noc-muted font-mono break-all">
                {target.names.slice(0, 5).join(", ")}
                {count > target.names.slice(0, 5).length ? " …" : ""}
              </div>
            )}
          </div>
        ) : (
          target.deviceId && (
            <div className="text-xs">
              <span className="text-noc-muted">Dispositivo:</span>{" "}
              <span className="font-mono text-noc-text">{target.deviceId}</span>
            </div>
          )
        )}

        {impact && <div className="text-xs text-noc-warn/90">{impact}</div>}
        {tenant && (
          <div className="text-[11px] text-noc-muted">
            Empresa: <span className="text-noc-text">{tenant}</span>
          </div>
        )}

        {collectReason && (
          <label className="block space-y-1">
            <span className="text-xs text-noc-muted">{reasonLabel}</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              className="w-full px-3 py-2 rounded-lg bg-noc-bg border border-noc-border
                         text-noc-text text-sm outline-none focus:border-noc-err/30 resize-none"
            />
          </label>
        )}

        {requirePassword && (
          <label className="block space-y-1">
            <span className="text-xs text-noc-muted">
              Confirme com {mode.label.toLowerCase()} para prosseguir:
            </span>
            <input
              ref={inputRef}
              type="password"
              autoComplete="off"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={mode.placeholder}
              className="w-full h-9 px-3 rounded-lg bg-noc-bg border border-noc-border
                         text-noc-text outline-none focus:border-noc-err/30 font-mono"
            />
          </label>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-3 py-1.5 rounded-lg border border-noc-border
                       text-noc-muted hover:text-noc-text"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canConfirm}
            className="text-sm px-3 py-1.5 rounded-lg bg-noc-err/15 border
                       border-noc-err/40 hover:bg-noc-err/25
                       text-noc-err font-semibold disabled:opacity-40
                       disabled:cursor-not-allowed"
          >
            Confirmar
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

export default ConfirmCriticalAction;
